import { formatAppDateTime, formatAppTime } from "../../app/utils/appTimeFormat";

export const P2P_STATUS_META = {
  Active: { label: "Active", tone: "live" },
  Pending: { label: "Pending", tone: "warn" },
  Settled: { label: "Settled", tone: "muted" },
  Voided: { label: "Voided", tone: "muted" },
};

export const P2P_OUTCOME_META = {
  Pending: { label: "Open", tone: "warn" },
  Won: { label: "Won", tone: "live" },
  Lost: { label: "Lost", tone: "muted" },
  Voided: { label: "Voided", tone: "muted" },
};

export const P2P_ROLE_META = {
  created: { label: "Created", tone: "live" },
  joined: { label: "Joined", tone: "muted" },
  invited: { label: "Invited", tone: "warn" },
  participant: { label: "In duel", tone: "muted" },
};

export function p2pStatusMeta(status) {
  return (
    P2P_STATUS_META[status] || {
      label: status || "—",
      tone: "muted",
    }
  );
}

export function p2pOutcomeMeta(status) {
  return (
    P2P_OUTCOME_META[status] || {
      label: status || "—",
      tone: "muted",
    }
  );
}

export function p2pRoleMeta(role) {
  return (
    P2P_ROLE_META[role] || {
      label: role || "—",
      tone: "muted",
    }
  );
}

export function p2pMatchInitials(home, away) {
  const h = String(home || "?").trim()[0] || "?";
  const a = String(away || "?").trim()[0] || "?";
  return (h + a).toUpperCase();
}

export function p2pFixtureStatusShort(duel) {
  const raw = duel?.fixtureStatus;
  if (!raw) return "NS";
  if (typeof raw === "string") return raw.trim().toUpperCase() || "NS";
  if (typeof raw === "object") {
    const short = String(raw.short || "").trim();
    if (short) return short.toUpperCase();
    const long = String(raw.long || "").trim();
    if (long) return long;
  }
  return "NS";
}

const LIVE_FIXTURE_CODES = new Set(["1H", "HT", "2H", "ET", "BT", "P", "LIVE", "INT"]);

export function p2pIsLiveFixture(duel) {
  return LIVE_FIXTURE_CODES.has(p2pFixtureStatusShort(duel));
}

/** Home · FT · Away — fixture status replaces "vs". */
export function p2pMatchHeadline(duel) {
  const home = String(duel?.matchHome || "").trim();
  const away = String(duel?.matchAway || "").trim();
  const status = p2pFixtureStatusShort(duel);

  if (home && away) return `${home} · ${status} · ${away}`;
  if (home || away) return [home, away].filter(Boolean).join(" · ");
  return duel?.matchLabel || "Match";
}

export function formatP2PKickoff(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return formatAppTime(d);
  }
  return formatAppDateTime(d);
}

export function formatP2PCreatedAt(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppDateTime(d);
}

/** Duel status chip for admin ledger (Active, Pending, Settled, …). */
export function p2pLedgerRowStatus(duel) {
  return p2pStatusMeta(duel.status);
}

export function p2pNeedsOutcome(duel) {
  return (
    (duel.status === "Active" || duel.status === "Pending") && duel.p2pStatus === "Pending"
  );
}

export function p2pContextLine(duel, { includeSelection = true } = {}) {
  const parts = [];

  if (includeSelection && duel.selection) parts.push(duel.selection);

  const joined = Number(duel.joinedPeople) || 0;
  const maxPeople = Number(duel.maxPeopleAllowed) || 0;
  if (maxPeople > 0) {
    parts.push(`${joined}/${maxPeople} joined`);
  } else if (joined > 0) {
    parts.push(`${joined} joined`);
  }

  const kickoff = formatP2PKickoff(duel.fixtureDate);
  if (kickoff) parts.push(kickoff);
  else parts.push(formatP2PCreatedAt(duel.createdAt));

  return parts.join(" · ");
}

export function p2pMetricsLine(duel, formatCompact) {
  const parts = [];
  const budget = Number(duel.creatorBudget ?? duel.poolTotal) || 0;
  const minStake = Number(duel.minStake ?? duel.perSeatStake ?? duel.stake) || 0;

  if (budget > 0) parts.push(`${formatCompact(budget)} budget`);
  if (minStake > 0) parts.push(`${formatCompact(minStake)} min stake`);

  return parts.join(" · ") || "No budget recorded";
}

export function p2pSummaryParts(summary) {
  if (!summary) return [];
  const parts = [];
  if (summary.active) parts.push(`${summary.active} active`);
  if (summary.pending) parts.push(`${summary.pending} pending`);
  if (summary.settled) parts.push(`${summary.settled} settled`);
  if (summary.voided) parts.push(`${summary.voided} voided`);
  return parts;
}

export function p2pParticipantRoleMeta(role) {
  switch (role) {
    case "creator":
      return { label: "Creator", tone: "live" };
    case "joined":
      return { label: "Joined", tone: "muted" };
    case "invited":
      return { label: "Invited", tone: "warn" };
    case "invite_pending":
      return { label: "Invite pending", tone: "warn" };
    default:
      return { label: role || "Participant", tone: "muted" };
  }
}

export function p2pPledgeStatsLine(stats, formatCompact) {
  if (!stats) return "—";
  const min = Number(stats.min) || 0;
  const avg = Number(stats.avg) || 0;
  const max = Number(stats.max) || 0;
  if (!min && !avg && !max) return "No pledges yet";
  if (min === max) return `${formatCompact(min)} pledge`;
  return `${formatCompact(min)} · ${formatCompact(avg)} avg · ${formatCompact(max)} max`;
}

export function p2pPledgeRangeLabel(stats, formatCompact) {
  if (!stats) return null;
  const min = Number(stats.min) || 0;
  const max = Number(stats.max) || 0;
  if (!min && !max) return null;
  if (min === max) return formatCompact(min);
  return `${formatCompact(min)}–${formatCompact(max)}`;
}
